import { Input } from '@frontend/shadcn/components/ui/input';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { PlusIcon, MinusIcon, FileLinesIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import React, { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { RichTextEditor } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import { useParams } from 'react-router-dom';
import { useAuth } from '@frontend/context/authContext';
import { message } from '@revfluence/fresh';
import { useGetTermsSettingsForProgram, useGetContentGuidelineTemplatesForProject } from '@frontend/app/hooks';
import { useRecruitment, useBriefPage } from '../RecruitmentContext';
import { RefreshButton } from './RefreshButton';

export const BriefPage = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    setBriefTitle,
    setBriefDescription,
    setBriefHasDescription,
    setBriefPrimaryButtonLabel,
  } = useRecruitment();

  const { projectId } = useParams<{ projectId: string }>();

  const { clientInfo } = useAuth();

  const briefPage = useBriefPage();

  const { refetch: refetchTermsSettings } = useGetTermsSettingsForProgram({
    variables: {
      programId: Number(projectId),
    },
    skip: !projectId,
  });

  const { refetch: refetchContentGuidelines } = useGetContentGuidelineTemplatesForProject({
    variables: {
      programId: Number(projectId),
    },
    skip: !projectId,
  });

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      await Promise.all([
        refetchTermsSettings(),
        refetchContentGuidelines(),
      ]);
      message.success('Brief settings refreshed successfully');
    } catch (error) {
      message.error('Failed to refresh brief settings');
      console.error('Error refreshing brief settings:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!briefPage) {
    return null;
  }

  const {
    title,
    description,
    hasDescription,
    primaryButtonLabel,
  } = briefPage;

  return (
    <Accordion type="multiple" className="[&_h3]:mb-0" defaultValue={['hero']}>
      <AccordionItem value="hero" className="border-b-0">
        <AccordionTrigger>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-5 rounded-lg text-primary-foreground flex items-center justify-center">
              <FileLinesIcon fontSize={12} />
            </div>
            <span className="text-base font-medium">Brief</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          <Alert>
            <AlertDescription>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1">
                  <div className="pr-1">
                    <CircleInfoIcon fontSize={16} />
                  </div>
                  <span className="font-medium">
                    Changes to the Brief can be applied in
                    {' '}
                    <a
                      className="underline text-foreground hover:text-foreground hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${window.location.origin}/client/${clientInfo?.id}/projects/${projectId}/settings/terms`}
                    >
                      Brief Settings.
                    </a>
                  </span>
                </div>
                <RefreshButton
                  onRefresh={handleRefresh}
                  isRefreshing={isRefreshing}
                />
              </div>
            </AlertDescription>
          </Alert>

          {/* Title Section */}
          <div className="flex flex-col gap-2 mt-2">
            <label htmlFor="title" className="text-xs font-medium text-foreground">Title <span className="text-primaryMuted font-normal">(Required)</span></label>
            <div className="px-[1px]">
              <Input id="title" placeholder="Campaign Brief" defaultValue="Campaign Brief" value={title || ''} onChange={(e) => setBriefTitle(e.target.value)} />
            </div>
          </div>

          {/* Description Section */}
          <div className="mt-2">
            {hasDescription && (
              <div className="flex flex-col items-start gap-2">
                <Button
                  variant="link"
                  className="gap-2 pl-0"
                  onClick={() => setBriefHasDescription(false)}
                  size="xs"
                >
                  <MinusIcon />
                  Remove Description
                </Button>
                <RichTextEditor
                  placeholder="Please review and agree to the details below to continue with your application."
                  value={description || 'Please review and agree to the details below to continue with your application.'}
                  onChange={setBriefDescription}
                  className="[&_.ql-editor]:min-h-[100px]"
                />
              </div>
            )}
            {!hasDescription && (
              <Button
                variant="link"
                className="gap-2 pl-0"
                onClick={() => setBriefHasDescription(true)}
                size="xs"
              >
                <PlusIcon />
                Add Description
              </Button>
            )}
          </div>

          <Separator className="my-4" />

          {/* Primary Button Label Section */}
          <div>
            <label htmlFor="buttonLabel" className="text-xs font-medium text-foreground">Primary Button Label <span className="text-primaryMuted">(Required)</span></label>
            <div className="px-[1px] pb-[1px]">
              <Input id="buttonLabel" placeholder="Continue" className="mt-2" value={primaryButtonLabel || 'Continue'} onChange={(e) => setBriefPrimaryButtonLabel(e.target.value)} />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
